import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck, Phone, ArrowRight, MapPin } from "lucide-react";
import { SERVICES, COMPANY_INFO } from "../data";
import Sidebar from "../components/Sidebar";

export default function Services() {
  return (
    <div className="pt-[72px] sm:pt-[128px]">
      {/* Hero Section */}
      <section className="relative bg-slate-950 py-24 sm:py-32 overflow-hidden text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/image00002.jpeg"
            alt="Vores ydelser"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/75 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 mb-6 uppercase tracking-wider">
            Professionelle Løsninger
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight uppercase">
            Mine Ydelser på Fyn
          </h1>
          <p className="text-lg text-slate-300 mt-4 max-w-2xl mx-auto leading-relaxed">
            Jeg tilbyder skræddersyede løsninger til både private og erhvervskunder. Altid med fokus på høj kvalitet, pålidelighed og stribefri resultater.
          </p>
        </div>
      </section>

      {/* Services Grid Section with Sidebar */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Content (lg:col-span-8) */}
            <div className="lg:col-span-8 space-y-20">
              {SERVICES.map((service, idx) => {
                return (
                  <div
                    key={service.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center border-b border-slate-100 pb-16 last:border-0 last:pb-0"
                  >
                    {/* Image Column */}
                    <div className="md:col-span-5">
                      <div className="relative group">
                        <div className="absolute -inset-2 bg-slate-100 rounded-md rotate-1 scale-95 opacity-70 z-0 group-hover:rotate-2 transition-transform duration-300" />
                        <img
                          src={service.image}
                          alt={service.title}
                          className="relative z-10 rounded-md shadow-md w-full h-[220px] sm:h-[260px] object-cover"
                        />
                      </div>
                    </div>

                    {/* Content Column */}
                    <div className="md:col-span-7 space-y-4">
                      <div>
                        <span className="text-sky-500 font-bold text-xs uppercase tracking-wider">
                          Ydelse {idx + 1} af {SERVICES.length}
                        </span>
                        <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-1 tracking-tight uppercase">
                          {service.title}
                        </h2>
                      </div>
                      
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {service.shortDescription}
                      </p>

                      {/* Benefits List */}
                      <ul className="space-y-2">
                        {service.benefits.slice(0, 2).map((benefit, bIdx) => (
                          <li key={bIdx} className="flex items-start gap-2 text-xs text-slate-700">
                            <ShieldCheck className="h-4.5 w-4.5 text-sky-500 shrink-0 mt-0.5" />
                            <span>{benefit}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="flex flex-wrap items-center gap-4 pt-2">
                        <Link
                          to={`/ydelser/${service.slug}`}
                          className="bg-sky-500 hover:bg-sky-600 text-white px-5 py-2.5 rounded-md text-xs font-bold uppercase tracking-wider transition-all shadow-sm inline-flex items-center gap-1.5"
                        >
                          Læs mere
                          <ChevronRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right Column: Sidebar (lg:col-span-4) */}
            <div className="lg:col-span-4">
              <Sidebar />
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-950 py-24 text-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/df307af1-4bd3-49b1-bcdd-d5b6dbccf628.jpg"
            alt="Solcellerens på Fyn"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/75 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-6 uppercase">
            Har du en specifik opgave på Fyn?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Jeg skræddersyr altid mine aftaler, så de passer præcis til dine ønsker og behov. Kontakt mig i dag for en uforpligtende snak og et godt tilbud.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-sky-500 hover:bg-sky-600 text-white px-8 py-4 rounded-md text-xs font-bold uppercase tracking-wider transition-all shadow-sm"
            >
              Få et uforpligtende tilbud
            </Link>
            {COMPANY_INFO.phone && (
              <a
                href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                className="w-full sm:w-auto border border-white hover:bg-white/10 text-white font-bold px-8 py-4 rounded-md transition-all text-sm tracking-wide uppercase inline-flex items-center justify-center gap-2"
              >
                <Phone className="h-5 w-5 text-sky-400" />
                <span>Ring til mig på {COMPANY_INFO.phone}</span>
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
